import React, { useEffect, useRef, useState } from "react";
import { Button, Flex } from "@mantine/core";
import { useTheme, useMediaQuery } from "@mui/material";
import { useUser } from "../contexts/UserContext";
import DataLoader from "../components/DataLoader";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../components/Container/PageContainer";
import { FriendsInteractionSection } from "../components/LifetimeAnalytic/FriendsInteractionSection";
import { TopFriendsSection } from "../components/LifetimeAnalytic/TopFriendsSection";
import { FriendConversationModal } from "../components/modal/FriendConversationModal";
import { useFriendsInteractionData } from "../hooks/useThreadsSelector";
import { useThreadsState, useThreadsDispatch, loadFriendsInteractionData } from "../contexts/ThreadsContext";

const FriendsInteraction = () => {
  const { t } = useTranslation();
  const { repliesByUser, isDataReady, error } = useFriendsInteractionData();
  const { lifetimeThreadsList } = useThreadsState();
  const dispatch = useThreadsDispatch();
  const loadStartedRef = useRef(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [sectionLoadingStarted, setSectionLoadingStarted] = useState(false);
  const theme = useTheme();
  const isXL = useMediaQuery(theme.breakpoints.down("xl"));
  const { isLogin } = useUser();

  const [opened, setOpened] = useState(false);
  const [selectedThreads, setSelectedThreads] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // 특정 사용자와의 대화 데이터 찾기 및 그루핑
  const getUserConversations = (username) => {
    const userInteraction = repliesByUser.find(
      (user) => user.username === username
    );
    if (!userInteraction || !userInteraction.conversations) {
      return [];
    }

    // 대화를 원글별로 그루핑
    const grouped = userInteraction.conversations.reduce(
      (acc, conversation) => {
        const threadId =
          conversation.threadId || conversation.threadText || "unknown";
        if (!acc[threadId]) {
          acc[threadId] = {
            threadText: conversation.threadText,
            conversations: [],
          };
        }
        acc[threadId].conversations.push(conversation);
        return acc;
      },
      {}
    );

    // 각 그룹 내에서 시간순 정렬 후 그룹을 배열로 변환
    return Object.values(grouped)
      .map((group) => ({
        ...group,
        conversations: group.conversations.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        ),
      }))
      .sort((a, b) => {
        // 각 그룹의 가장 최신 댓글 기준으로 정렬
        const latestA = Math.max(
          ...a.conversations.map((c) => new Date(c.timestamp))
        );
        const latestB = Math.max(
          ...b.conversations.map((c) => new Date(c.timestamp))
        );
        return latestB - latestA;
      });
  };

  const handleUserClick = (username) => {
    const userConversationGroups = getUserConversations(username);
    const userInteraction = repliesByUser.find((u) => u.username === username);

    setSelectedThreads(userConversationGroups);
    setSelectedUser(username);
    setSelectedUserId(userInteraction?.userId || null);
    setOpened(true);
  };

  // 라우트 진입 시 수동 로딩 (글로벌 로딩에서 제외됨)
  useEffect(() => {
    if (loadStartedRef.current) return;
    if (!Array.isArray(lifetimeThreadsList) || lifetimeThreadsList.length === 0) return;
    if (isDataReady) return;

    loadStartedRef.current = true;
    setSectionLoadingStarted(true);
    loadFriendsInteractionData(dispatch, lifetimeThreadsList).catch(() => {
      // 에러는 섹션 에러 상태로 전달되므로 여기서는 무시
    });
  }, [dispatch, lifetimeThreadsList, isDataReady]);

  const handleManualLoad = async () => {
    if (!Array.isArray(lifetimeThreadsList) || lifetimeThreadsList.length === 0) return;
    try {
      setManualLoading(true);
      setSectionLoadingStarted(true);
      await loadFriendsInteractionData(dispatch, lifetimeThreadsList);
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <PageContainer>
      <DataLoader
        msg={t("loading.friends_interaction")}
        isLoad={(sectionLoadingStarted && !isDataReady && !error) || manualLoading}
        isLogin={isLogin}
        isXL={isXL}
        isPublicRoute={false}
        showElapsedTime={true}
      />
      {/* <Flex w="100%" justify="flex-end" mb={16}>
        <Button variant="light" loading={manualLoading} onClick={handleManualLoad}>
          친구 상호작용 로딩
        </Button>
      </Flex> */}
      <TopFriendsSection onUserClick={handleUserClick} />
      <FriendsInteractionSection onUserClick={handleUserClick} />

      {/* 친구 대화 모달 */}
      <FriendConversationModal
        opened={opened}
        onClose={() => setOpened(false)}
        selectedUser={selectedUser}
        selectedUserId={selectedUserId}
        selectedThreads={selectedThreads}
      />
    </PageContainer>
  );
};

export default FriendsInteraction; 